import React from 'react';
import { connect } from 'react-redux';
import {
    FormGroup, Progress,
} from 'reactstrap';
import { isEqual, debounce } from 'lodash-es';
import AsyncSelect from 'react-select/async';
import { faTrashAlt, faUser } from '@fortawesome/free-solid-svg-icons';
import { FormPrompt } from '@s20/components/common/FormPrompt';
import { getAllTenants } from '@s20/selectors/features/common.selectors';
import { clone } from '@s20/common/util/object';
import { httpClient } from '@s20/services/httpClient';
import {
    AppPageForm, FormLabel, FormDivider, FlexCenterRow, toasty,
} from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import authService from '../api-authorization/AuthorizeService';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import { SaveButton } from '../common/forms/SaveButton';
import { ConfirmDelete } from '../common/Modal';
import { Delegate } from './Delegate';


const notifyError = (message) => toasty.error('Save Unsuccessful', message);

class DelegateForm extends React.Component {
    static contextType = CommonContext;
    constructor(props) {
        super(props);

        this.state = {
            groups: [],
            expandedDispatchOptions: [],
            delegate: new Delegate(),
            selectedUser: null,
            loading: true,
        };

        this.onSubmit = this.onSubmit.bind(this);
        this.onDeleteDelegateConfirmed = this.onDeleteDelegateConfirmed.bind(this);
    }

    componentDidMount() {
        this.populateState();
    }

    componentDidUpdate(prevProps) {
        if (prevProps && (this.props.match.params.id !== (prevProps.match.params ?? {}).id)) {
            this.populateState();
        }
    }

    onClearErrors = () => this.setState({ errors: {} });

    async populateState() {
        const isAuthenticated = await authService.isAuthenticated();
        if (isAuthenticated) {
            let delegate = null;
            const { id } = { ...this.props.match.params };
            const [groups, expandedDispatchOptions, delegateResponse] = await Promise.all([
                httpClient.js(ApiRoutes.auth.groups()),
                httpClient
                    .js(ApiRoutes.typeAheads.dispatchCompaniesForDelegate(id))
                    .then((offices) => (offices ?? []).map((x) => ({ id: x.value, companyName: x.label }))),
                id
                    ? httpClient.get(ApiRoutes.delegate.byId(id), httpClient.format.none)
                    : new Delegate(),
            ]);

            delegate = id
                ? await delegateResponse.json()
                : delegateResponse;

            const selectedUser = id
                ? {
                    value: delegate.userId,
                    label: delegate.userName,
                }
                : null;

            const originalData = clone(delegate);

            this.setState({
                originalData,
                delegate,
                loading: false,
                groups,
                saving: false,
                expandedDispatchOptions,
                selectedUser,
            });
        }
    }

    onTenantChange = (selection) => {
        const { delegate } = { ...this.state };
        delegate.tenantId = selection?.id;
        this.setState({ delegate });
    };

    onGroupChange = (value) => {
        const { delegate } = { ...this.state };
        delegate.applicationUserGroups = (value ?? []).map((x) => x.id) ?? [];
        this.setState({ delegate });
    };

    onExpandedDispatchCompaniesChanged = (values) => {
        const { delegate } = { ...this.state };
        if ((values ?? []).length > 0) delegate.dispatchCompanies = values.map((x) => x.id);
        else delegate.dispatchCompanies = [];
        this.setState({ delegate });
    };

    loadDelegateOptions = debounce((inputValue, callback) => {
        const { tenantId } = this.state.delegate;
        if (!tenantId) {
            callback([]);
            toasty.error('Please select Tenant.');
            return;
        }
        fetch(ApiRoutes.typeAheads.allEmployees(tenantId, inputValue))
            .then((response) => response.json())
            .then((data) => {
                const options = data.map((item) => ({
                    label: item.label,
                    value: item.value,
                }));
                callback(options);
            });
    }, 250);

    onUserChanged = (value) => {
        this.setState((prevState) => ({
            delegate: {
                ...prevState.delegate,
                userId: value?.value ?? null,
            },
            selectedUser: value,
        }));
    };

    setSaving = (b) => this.setState({ saving: b });

    onSubmit = async () => {
        this.onClearErrors();
        this.setSaving(true);

        const { delegate } = { ...this.state };
        const cloned = clone(delegate);

        if (this.props.location.pathname == AppNavPaths.DelegateNew) {
            await this.createDelegate(cloned);
        } else {
            await this.updateDelegate(cloned);
        }
    };


    createDelegate = async (delegate) => {
        const response = await httpClient
            .post(ApiRoutes.delegate.create(), delegate)
            .catch(this.handleSaveError);
        if (response) {
            if (response.result === 'SUCCESS') {
                toasty.success('Delegate created.');
                this.context.history.push(`${AppNavPaths.Delegate}/${response.data}`);
            } else {
                notifyError(response.message);
                this.setState({ saving: false });
            }
        } else {
            this.setState({ saving: false });
        }
    };

    updateDelegate = async (delegate) => {
        const result = await httpClient
            .put(ApiRoutes.delegate.update(), delegate)
            .catch(this.handleSaveError);
        if (result) {
            if (result.result === 'SUCCESS') {
                toasty.success('Delegate updated.');
                this.populateState();
                this.context.history.replace(this.props.location.pathname);
            } else {
                notifyError(result.message);
                this.setState({ saving: false });
            }
        } else {
            this.setState({ saving: false });
        }
    };

    onDeleteDelegateConfirmed = async () => {
        const { id } = { ...this.props.match.params };
        const result = await httpClient
            .delete(ApiRoutes.delegate.delete(id))
            .catch(this.handleSaveError);

        if (result) {
            toasty.success('Delegate deleted.');
            this.context.history.push(`${AppNavPaths.Delegates}`);
        }
    };

    clearSaving = () => this.setState({ saving: false, errors: {} });

    getActions = () => {
        const delegateId = this.props.match?.params?.id;

        const actions = [];

        if (delegateId) {
            actions.push({
                key: 'delete',
                onClick: this.showDeleteModal,
                icon: faTrashAlt,
                label: 'Delete Delegate',
                className: 'text-danger',
                CanProps: {
                    I: 'delete',
                    a: 'employee',
                },
            });
        }

        if (actions.length > 0) {
            return actions;
        }

        return null;
    };

    showDeleteModal = () => {
        ConfirmDelete.show({
            title: 'Delete Delegate',
            onSubmit: this.onDeleteDelegateConfirmed,
            body: () => 'Are you sure you want to delete this delegate?',
        });
    };

    render() {
        const {
            groups, delegate, saving, originalData, expandedDispatchOptions, loading,
        } = { ...this.state };

        const { tenant } = { ...this.context };

        const delegateId = this.props.match?.params?.id;

        //should not show current tenant
        const tenantOptions = (this.props.tenants ?? []).filter(
            (x) => x.id !== tenant.id,
        ) ?? [];

        const selectedExpandedDispatchCompanies = expandedDispatchOptions.filter((x) => (delegate?.dispatchCompanies ?? []).includes(x.id));

        if (
            loading
            || !expandedDispatchOptions.length
            || !groups.length
            || !tenantOptions.length
        ) {
            return <Progress />;
        }

        return (
            <>
                <FormPrompt
                    when={!saving && !isEqual(originalData, delegate)}
                    message="You have unsaved changes, are you sure you want to leave?"
                />

                <AppPageForm
                    formShown={this.context.formIsOpen}
                    formId="delegateForm"
                    formHeadingIcon={faUser}
                    formHeading={!delegateId ? 'New Delegate' : 'Edit Delegate'}
                    formName="delegateForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ formValidated: value });
                    }}
                    isValidated={this.state.formValidated}
                    saving={saving}
                    errors={this.state.errors}
                    loading={this.state.loading}
                    backUrl={AppNavPaths.Delegates}
                    backLabel="Back to Delegates"
                    actions={this.getActions()}
                >
                    {delegateId ? (
                        <>
                            <FormGroup>
                                <FormLabel
                                    htmlFor="Tenant"
                                    label="Tenant"
                                />
                                <div className="form-control-plaintext">{delegate.tenantName ?? 'N/A'}</div>
                            </FormGroup>
                            <FormGroup>
                                <FormLabel
                                    htmlFor="Username"
                                    label="Username"
                                />
                                <div className="form-control-plaintext">{this.state.selectedUser?.label ?? 'N/A'}</div>
                            </FormGroup>
                        </>
                    ) : (
                        <>
                            <ValidatedSelect
                                id="tenantId"
                                name="delegate.tenantId"
                                label="Tenant"
                                required
                                options={tenantOptions}
                                value={
                                    tenantOptions?.find(
                                        (s) => s.id
                                            === delegate.tenantId,
                                    ) ?? ''
                                }
                                getOptionLabel={(option) => option.companyName}
                                getOptionValue={(option) => option.id}
                                onChange={this.onTenantChange}
                                error="Tenant selection is required."
                            />

                            <ValidatedSelect
                                component={AsyncSelect}
                                label="Search User"
                                key={delegate.tenantId ?? 'no-tenant'}
                                loadOptions={this.loadDelegateOptions}
                                placeholder={
                                    delegate.tenantId ? 'Type Employee Name or Username Here' : 'Select a tenant first'
                                }
                                id="selectedUser"
                                name="selectedUser"
                                value={this.state.selectedUser}
                                onChange={this.onUserChanged}
                                isClearable
                                cacheOptions
                                required
                                error="A tenant must be selected"
                                isDisabled={!!delegateId}
                            />
                        </>
                    )}

                    <ValidatedSelect
                        id="applicationUserGroups"
                        name="applicationUserGroups"
                        label="Groups"
                        required
                        isMulti
                        options={groups}
                        value={
                            (groups ?? []).filter((x) => (delegate.applicationUserGroups ?? []).includes(
                                x.id,
                            )) ?? ''
                        }
                        getOptionLabel={(option) => option.groupName}
                        getOptionValue={(option) => option.id}
                        error="Group selection is required."
                        onChange={this.onGroupChange}
                    />

                    <ValidatedSelect
                        id="dispatchCompanies"
                        name="dispatchCompanies"
                        label="Dispatch Offices"
                        options={
                            expandedDispatchOptions
                        }
                        isMulti
                        required
                        value={
                            selectedExpandedDispatchCompanies
                        }
                        getOptionValue={(option) => option.id}
                        getOptionLabel={(option) => option.companyName}
                        onChange={this.onExpandedDispatchCompaniesChanged}
                        error="Dispatch Office selection is required."
                        onSelectAll={() => this.onExpandedDispatchCompaniesChanged(this.state.expandedDispatchOptions)}
                    />

                    <FormDivider />
                    <FlexCenterRow className="mb-3">
                        <SaveButton
                            label="Save Delegate"
                            saving={this.state.saving}
                        />
                    </FlexCenterRow>
                </AppPageForm>
            </>
        );
    }
}

const mapStateToProps = (state) => ({
    tenants: getAllTenants(state),
});

export default connect(mapStateToProps)(DelegateForm);